import { forwardRef, useId } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { controlClasses, describedBy, Field } from './Field';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label?: React.ReactNode;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  /** Convenience for flat option lists; ignored when children are supplied. */
  options?: SelectOption[];
  placeholder?: string;
  selectSize?: 'sm' | 'md' | 'lg';
  containerClassName?: string;
}

const SIZES = {
  sm: 'h-8 text-sm',
  md: 'h-9 text-sm',
  lg: 'h-11 text-base',
} as const;

/**
 * A native <select>. Deliberately not a custom listbox: on a phone at the front
 * desk the OS picker is faster and more reliable than anything we would build.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    label,
    hint,
    error,
    options,
    placeholder,
    selectSize = 'md',
    className,
    containerClassName,
    id,
    required,
    children,
    ...props
  },
  ref,
) {
  const generatedId = useId();
  const selectId = id ?? generatedId;
  const invalid = Boolean(error);

  return (
    <Field
      label={label}
      htmlFor={selectId}
      hint={hint}
      error={error}
      required={required}
      className={containerClassName}
    >
      <div className="relative flex items-center">
        <select
          ref={ref}
          id={selectId}
          required={required}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy(selectId, invalid, Boolean(hint))}
          className={cn(controlClasses, SIZES[selectSize], 'appearance-none pr-8', className)}
          {...props}
        >
          {placeholder ? (
            <option value="" disabled={required}>
              {placeholder}
            </option>
          ) : null}
          {children ??
            options?.map((opt) => (
              <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                {opt.label}
              </option>
            ))}
        </select>
        <ChevronDown
          aria-hidden="true"
          className="pointer-events-none absolute right-2.5 h-4 w-4 text-fg-subtle"
        />
      </div>
    </Field>
  );
});
